/*
 * Sudoku engine, part 2 of 3 — the solver that works the way a person does.
 *
 *   SudokuEngine.state(geo, values, masks)   a position: digits + candidates
 *   SudokuEngine.nextStep(state, opts)       the easiest deduction available, or null
 *   SudokuEngine.applyStep(state, step)      make it
 *   SudokuEngine.grade(geo, values, opts)    solve by logic alone and say what it took
 *   SudokuEngine.TECHNIQUES / TIER           every technique and the level it belongs to
 *
 * A step is plain data — which technique, which cells form the pattern, what
 * gets placed or crossed out — and never a sentence: the page turns it into
 * words in the reader's language, the tests check it against the solution.
 *
 * Every technique here is a proof, not a guess: a step never removes the digit
 * that belongs in a cell. `unique_rectangle` is the one that leans on "the
 * puzzle has a single solution", so it is switched on only for a board where
 * that was proven, and the generator never uses it. `forcing` and `brute` exist
 * for somebody else's puzzle that our pattern library cannot finish; a puzzle
 * we generate never needs them (tiers 1–5 only).
 */
(function (root) {
    'use strict';

    var S = root.SudokuEngine;
    var POP = S.POP, LOW = S.LOW, digitsOf = S.digitsOf;

    // Level a technique belongs to. A puzzle's grade is the highest tier its
    // easiest solution needs: 1 easy · 2 medium · 3 hard · 4 expert · 5 master.
    // 6 and 7 are "beyond the levels" — they are never generated.
    var TECHNIQUES = [
        ['full_house', 1], ['hidden_single', 1],
        ['naked_single', 2],
        ['pointing', 3], ['claiming', 3], ['naked_pair', 3], ['hidden_pair', 3],
        ['naked_triple', 4], ['hidden_triple', 4], ['x_wing', 4], ['skyscraper', 4],
        ['two_string_kite', 4], ['xy_wing', 4], ['naked_quad', 4], ['hidden_quad', 4],
        ['swordfish', 5], ['w_wing', 5], ['simple_coloring', 5], ['xyz_wing', 5],
        ['jellyfish', 5], ['xy_chain', 5],
        ['unique_rectangle', 6], ['forcing', 6],
        ['brute', 7]
    ];
    var TIER = {};
    TECHNIQUES.forEach(function (t) { TIER[t[0]] = t[1]; });

    // How much thinking a step costs, on the scale HoDoKu made common. Summed
    // over a solution it tells two puzzles of the same level apart.
    var WEIGHT = {
        full_house: 4, hidden_single: 14, naked_single: 24,
        pointing: 50, claiming: 50, naked_pair: 60, hidden_pair: 70,
        naked_triple: 80, hidden_triple: 100, x_wing: 140, skyscraper: 130,
        two_string_kite: 150, xy_wing: 160, naked_quad: 120, hidden_quad: 150,
        swordfish: 150, w_wing: 150, simple_coloring: 150, xyz_wing: 180,
        jellyfish: 160, xy_chain: 260,
        unique_rectangle: 100, forcing: 500, brute: 1000
    };

    // ── Position ─────────────────────────────────────────────────────────────
    // masks (optional): per-cell limit on the candidates — what the player's
    // notes still allow. A zero or missing entry means "no opinion".
    function state(geo, values, masks) {
        var cells = geo.cells, full = geo.full, n = geo.n;
        var val = new Int8Array(cells), cand = new Uint16Array(cells);
        var rowU = new Uint16Array(n), colU = new Uint16Array(n), boxU = new Uint16Array(n);
        var i, left = 0;
        for (i = 0; i < cells; i++) {
            var d = values[i] | 0;
            val[i] = d;
            if (d) {
                var bit = 1 << (d - 1);
                rowU[geo.cellRow[i]] |= bit; colU[geo.cellCol[i]] |= bit; boxU[geo.cellBox[i]] |= bit;
            }
        }
        for (i = 0; i < cells; i++) {
            if (val[i]) continue;
            left++;
            var m = full & ~(rowU[geo.cellRow[i]] | colU[geo.cellCol[i]] | boxU[geo.cellBox[i]]);
            if (masks && masks[i]) m &= masks[i];
            cand[i] = m;
        }
        return { geo: geo, val: val, cand: cand, left: left, unique: false };
    }

    function cloneState(st) {
        return { geo: st.geo, val: new Int8Array(st.val), cand: new Uint16Array(st.cand), left: st.left, unique: st.unique };
    }

    function place(st, cell, d) {
        var bit = 1 << (d - 1), peers = st.geo.peers[cell];
        st.val[cell] = d;
        st.cand[cell] = 0;
        st.left--;
        for (var k = 0; k < peers.length; k++) st.cand[peers[k]] &= ~bit;
    }

    function applyStep(st, step) {
        var k;
        if (step.place) for (k = 0; k < step.place.length; k++) place(st, step.place[k][0], step.place[k][1]);
        if (step.elim) for (k = 0; k < step.elim.length; k++) st.cand[step.elim[k][0]] &= ~step.elim[k][1];
    }

    // A digit with no cell left in a unit, or an empty cell with no candidate.
    function broken(st) {
        var geo = st.geo, n = geo.n, u, k;
        for (k = 0; k < geo.cells; k++) if (!st.val[k] && !st.cand[k]) return { kind: 'cell', cell: k };
        for (u = 0; u < geo.units.length; u++) {
            var unit = geo.units[u], have = 0;
            for (k = 0; k < n; k++) have |= st.val[unit[k]] ? (1 << (st.val[unit[k]] - 1)) : st.cand[unit[k]];
            if (have !== geo.full) return { kind: 'unit', unit: u, digit: LOW[geo.full & ~have] };
        }
        return null;
    }

    function combos(count, k, fn) {
        var idx = [];
        function rec(start, depth) {
            if (depth === k) return fn(idx);
            for (var i = start; i <= count - (k - depth); i++) {
                idx[depth] = i;
                if (rec(i + 1, depth + 1)) return true;
            }
            return false;
        }
        return rec(0, 0);
    }

    function cellsWith(st, list, bit) {
        var out = [];
        for (var k = 0; k < list.length; k++) if (st.cand[list[k]] & bit) out.push(list[k]);
        return out;
    }

    // Cells (outside `skip`) that still hold `bit` and are seen by every cell of `from`.
    function commonPeersWith(st, from, bit, skip) {
        var geo = st.geo, out = [], first = geo.peers[from[0]];
        for (var k = 0; k < first.length; k++) {
            var c = first[k];
            if (!(st.cand[c] & bit) || skip.indexOf(c) >= 0) continue;
            var ok = true;
            for (var j = 1; j < from.length; j++) if (!geo.sees[from[j] * geo.cells + c]) { ok = false; break; }
            if (ok) out.push(c);
        }
        return out;
    }

    function elimList(cells, mask) {
        return cells.map(function (c) { return [c, mask]; });
    }

    // ── Tier 1–2: singles ────────────────────────────────────────────────────
    function fullHouse(st) {
        var geo = st.geo, n = geo.n;
        for (var u = 0; u < geo.units.length; u++) {
            var unit = geo.units[u], empty = -1, count = 0;
            for (var k = 0; k < n; k++) if (!st.val[unit[k]]) { empty = unit[k]; count++; }
            if (count === 1 && POP[st.cand[empty]] === 1) {
                return { tech: 'full_house', unit: u, cells: [empty], digits: [LOW[st.cand[empty]]], place: [[empty, LOW[st.cand[empty]]]] };
            }
        }
        return null;
    }

    // Boxes first: scanning a box for the one free cell a digit can take is the
    // move a person makes without a single note.
    function hiddenSingle(st) {
        var geo = st.geo, n = geo.n, order = [], u;
        for (u = 2 * n; u < 3 * n; u++) order.push(u);
        for (u = 0; u < 2 * n; u++) order.push(u);
        for (var o = 0; o < order.length; o++) {
            u = order[o];
            var unit = geo.units[u], once = 0, twice = 0, k;
            for (k = 0; k < n; k++) {
                var m = st.cand[unit[k]];
                twice |= once & m;
                once |= m;
            }
            var single = once & ~twice;
            if (!single) continue;
            var d = LOW[single], bit = 1 << (d - 1);
            for (k = 0; k < n; k++) {
                if (st.cand[unit[k]] & bit) {
                    return { tech: 'hidden_single', unit: u, cells: [unit[k]], digits: [d], place: [[unit[k], d]] };
                }
            }
        }
        return null;
    }

    function nakedSingle(st) {
        for (var k = 0; k < st.geo.cells; k++) {
            if (!st.val[k] && POP[st.cand[k]] === 1) {
                return { tech: 'naked_single', cells: [k], digits: [LOW[st.cand[k]]], place: [[k, LOW[st.cand[k]]]] };
            }
        }
        return null;
    }

    // ── Tier 3: intersections and pairs ──────────────────────────────────────
    // Pointing: inside a box a digit is confined to one line → it leaves the
    // rest of that line. Claiming: inside a line it is confined to one box →
    // it leaves the rest of that box.
    function locked(st, pointing) {
        var geo = st.geo, n = geo.n;
        var from = pointing ? 2 * n : 0, to = pointing ? 3 * n : 2 * n;
        for (var u = from; u < to; u++) {
            var unit = geo.units[u];
            for (var d = 1; d <= n; d++) {
                var bit = 1 << (d - 1), cells = cellsWith(st, unit, bit);
                if (cells.length < 2) continue;
                var other = -1, k;
                if (pointing) {
                    var sameRow = true, sameCol = true;
                    for (k = 1; k < cells.length; k++) {
                        if (geo.cellRow[cells[k]] !== geo.cellRow[cells[0]]) sameRow = false;
                        if (geo.cellCol[cells[k]] !== geo.cellCol[cells[0]]) sameCol = false;
                    }
                    if (sameRow) other = geo.cellRow[cells[0]];
                    else if (sameCol) other = n + geo.cellCol[cells[0]];
                } else {
                    var sameBox = true;
                    for (k = 1; k < cells.length; k++) if (geo.cellBox[cells[k]] !== geo.cellBox[cells[0]]) sameBox = false;
                    if (sameBox) other = 2 * n + geo.cellBox[cells[0]];
                }
                if (other < 0) continue;
                var targets = cellsWith(st, geo.units[other], bit).filter(function (c) { return cells.indexOf(c) < 0; });
                if (!targets.length) continue;
                return {
                    tech: pointing ? 'pointing' : 'claiming', unit: u, unit2: other,
                    cells: cells, digits: [d], elim: elimList(targets, bit)
                };
            }
        }
        return null;
    }

    var SUBSET_NAME = { 2: 'pair', 3: 'triple', 4: 'quad' };

    // k cells of a unit that hold only k digits between them: those digits
    // belong to these cells and leave every other cell of the unit.
    function nakedSubset(st, k) {
        var geo = st.geo, n = geo.n, found = null;
        for (var u = 0; u < geo.units.length && !found; u++) {
            var unit = geo.units[u], pool = [], j;
            for (j = 0; j < n; j++) {
                var c = unit[j];
                if (!st.val[c] && POP[st.cand[c]] >= 2 && POP[st.cand[c]] <= k) pool.push(c);
            }
            if (pool.length < k) continue;
            var empties = 0;
            for (j = 0; j < n; j++) if (!st.val[unit[j]]) empties++;
            if (empties <= k) continue;
            (function (u, unit, pool) {
                combos(pool.length, k, function (idx) {
                    var mask = 0, cells = [], i;
                    for (i = 0; i < k; i++) { mask |= st.cand[pool[idx[i]]]; cells.push(pool[idx[i]]); }
                    if (POP[mask] !== k) return false;
                    var elim = [], seen = {};
                    // The same cells may share a second unit (a pair inside a box that
                    // also lies on one line): the digits leave that unit as well.
                    var shared = [u];
                    var r0 = geo.cellRow[cells[0]], c0 = geo.cellCol[cells[0]], b0 = geo.cellBox[cells[0]];
                    var sameR = true, sameC = true, sameB = true;
                    for (i = 1; i < k; i++) {
                        if (geo.cellRow[cells[i]] !== r0) sameR = false;
                        if (geo.cellCol[cells[i]] !== c0) sameC = false;
                        if (geo.cellBox[cells[i]] !== b0) sameB = false;
                    }
                    if (sameR && shared.indexOf(r0) < 0) shared.push(r0);
                    if (sameC && shared.indexOf(n + c0) < 0) shared.push(n + c0);
                    if (sameB && shared.indexOf(2 * n + b0) < 0) shared.push(2 * n + b0);
                    for (var s = 0; s < shared.length; s++) {
                        var list = geo.units[shared[s]];
                        for (i = 0; i < n; i++) {
                            var t = list[i];
                            if (st.val[t] || cells.indexOf(t) >= 0 || seen[t]) continue;
                            if (st.cand[t] & mask) { seen[t] = 1; elim.push([t, st.cand[t] & mask]); }
                        }
                    }
                    if (!elim.length) return false;
                    found = {
                        tech: 'naked_' + SUBSET_NAME[k], unit: u, unit2: shared.length > 1 ? shared[1] : -1,
                        cells: cells, digits: digitsOf(mask), elim: elim
                    };
                    return true;
                });
            })(u, unit, pool);
        }
        return found;
    }

    // k digits of a unit that fit only into the same k cells: those cells hold
    // nothing else.
    function hiddenSubset(st, k) {
        var geo = st.geo, n = geo.n, found = null;
        for (var u = 0; u < geo.units.length && !found; u++) {
            var unit = geo.units[u], pos = [], digits = [], d, j;
            var empties = 0;
            for (j = 0; j < n; j++) if (!st.val[unit[j]]) empties++;
            if (empties <= k) continue;
            for (d = 1; d <= n; d++) {
                var bit = 1 << (d - 1), p = 0;
                for (j = 0; j < n; j++) if (st.cand[unit[j]] & bit) p |= 1 << j;
                if (POP[p] >= 2 && POP[p] <= k) { pos.push(p); digits.push(d); }
            }
            if (digits.length < k) continue;
            (function (u, unit, pos, digits) {
                combos(digits.length, k, function (idx) {
                    var where = 0, mask = 0, i;
                    for (i = 0; i < k; i++) { where |= pos[idx[i]]; mask |= 1 << (digits[idx[i]] - 1); }
                    if (POP[where] !== k) return false;
                    var cells = [], elim = [];
                    for (i = 0; i < n; i++) {
                        if (!(where & (1 << i))) continue;
                        cells.push(unit[i]);
                        var extra = st.cand[unit[i]] & ~mask;
                        if (extra) elim.push([unit[i], extra]);
                    }
                    if (!elim.length) return false;
                    found = { tech: 'hidden_' + SUBSET_NAME[k], unit: u, cells: cells, digits: digitsOf(mask), elim: elim };
                    return true;
                });
            })(u, unit, pos, digits);
        }
        return found;
    }

    // ── Fish: X-Wing (2), Swordfish (3), Jellyfish (4) ───────────────────────
    // In k lines a digit sits in the same k crossing lines only → in those
    // crossing lines it lives inside the pattern and leaves every other cell.
    var FISH_NAME = { 2: 'x_wing', 3: 'swordfish', 4: 'jellyfish' };

    function fish(st, k) {
        var geo = st.geo, n = geo.n, found = null;
        if (k > n / 2) return null;
        for (var d = 1; d <= n && !found; d++) {
            var bit = 1 << (d - 1);
            for (var byRows = 1; byRows >= 0 && !found; byRows--) {
                var base = byRows ? 0 : n, cover = byRows ? n : 0;
                var lines = [], masks = [], u, j;
                for (u = 0; u < n; u++) {
                    var line = geo.units[base + u], p = 0;
                    for (j = 0; j < n; j++) if (st.cand[line[j]] & bit) p |= 1 << j;
                    if (POP[p] >= 2 && POP[p] <= k) { lines.push(u); masks.push(p); }
                }
                if (lines.length < k) continue;
                (function (d, bit, base, cover, lines, masks) {
                    combos(lines.length, k, function (idx) {
                        var where = 0, i, j2;
                        for (i = 0; i < k; i++) where |= masks[idx[i]];
                        if (POP[where] !== k) return false;
                        var baseSet = [], cells = [], elim = [], covers = [];
                        for (i = 0; i < k; i++) baseSet.push(lines[idx[i]]);
                        for (j2 = 0; j2 < n; j2++) {
                            if (!(where & (1 << j2))) continue;
                            covers.push(cover + j2);
                            var cl = geo.units[cover + j2];
                            for (i = 0; i < n; i++) {
                                if (!(st.cand[cl[i]] & bit)) continue;
                                if (baseSet.indexOf(i) >= 0) cells.push(cl[i]);
                                else elim.push([cl[i], bit]);
                            }
                        }
                        if (!elim.length) return false;
                        found = {
                            tech: FISH_NAME[k], digits: [d], cells: cells, elim: elim,
                            base: baseSet.map(function (x) { return base + x; }), cover: covers
                        };
                        return true;
                    });
                })(d, bit, base, cover, lines, masks);
            }
        }
        return found;
    }

    // ── Single-digit patterns built from two strong links ────────────────────
    // A strong link: a unit where the digit has exactly two cells left — one of
    // them is the digit for sure.
    function strongLinks(st, bit, from, to) {
        var geo = st.geo, out = [];
        for (var u = from; u < to; u++) {
            var cells = cellsWith(st, geo.units[u], bit);
            if (cells.length === 2) out.push({ unit: u, a: cells[0], b: cells[1] });
        }
        return out;
    }

    // Skyscraper: two parallel lines, each with a strong link, standing on a
    // shared crossing line. One of the two free ends is the digit.
    function skyscraper(st) {
        var geo = st.geo, n = geo.n;
        for (var d = 1; d <= n; d++) {
            var bit = 1 << (d - 1);
            for (var byRows = 1; byRows >= 0; byRows--) {
                var links = byRows ? strongLinks(st, bit, 0, n) : strongLinks(st, bit, n, 2 * n);
                var cross = byRows ? geo.cellCol : geo.cellRow;
                for (var i = 0; i < links.length; i++) {
                    for (var j = i + 1; j < links.length; j++) {
                        var p = links[i], q = links[j], ends = null;
                        if (cross[p.a] === cross[q.a] && cross[p.b] !== cross[q.b]) ends = [p.a, q.a, p.b, q.b];
                        else if (cross[p.b] === cross[q.b] && cross[p.a] !== cross[q.a]) ends = [p.b, q.b, p.a, q.a];
                        else if (cross[p.a] === cross[q.b] && cross[p.b] !== cross[q.a]) ends = [p.a, q.b, p.b, q.a];
                        else if (cross[p.b] === cross[q.a] && cross[p.a] !== cross[q.b]) ends = [p.b, q.a, p.a, q.b];
                        if (!ends) continue;
                        var all = [ends[0], ends[1], ends[2], ends[3]];
                        var targets = commonPeersWith(st, [ends[2], ends[3]], bit, all);
                        if (!targets.length) continue;
                        return {
                            tech: 'skyscraper', digits: [d], cells: all, base: [p.unit, q.unit],
                            roof: [ends[2], ends[3]], floor: [ends[0], ends[1]], elim: elimList(targets, bit)
                        };
                    }
                }
            }
        }
        return null;
    }

    // 2-String Kite: a strong link in a row and one in a column, tied together
    // by two ends that share a box. One of the two far ends is the digit.
    function twoStringKite(st) {
        var geo = st.geo, n = geo.n;
        for (var d = 1; d <= n; d++) {
            var bit = 1 << (d - 1);
            var rows = strongLinks(st, bit, 0, n), cols = strongLinks(st, bit, n, 2 * n);
            for (var i = 0; i < rows.length; i++) {
                for (var j = 0; j < cols.length; j++) {
                    var r = rows[i], c = cols[j];
                    var pairs = [[r.a, r.b, c.a, c.b], [r.a, r.b, c.b, c.a], [r.b, r.a, c.a, c.b], [r.b, r.a, c.b, c.a]];
                    for (var k = 0; k < 4; k++) {
                        var near1 = pairs[k][0], far1 = pairs[k][1], near2 = pairs[k][2], far2 = pairs[k][3];
                        if (near1 === near2 || near1 === far2 || far1 === near2 || far1 === far2) continue;
                        if (geo.cellBox[near1] !== geo.cellBox[near2]) continue;
                        if (geo.cellBox[far1] === geo.cellBox[near1] || geo.cellBox[far2] === geo.cellBox[near1]) continue;
                        var all = [near1, near2, far1, far2];
                        var targets = commonPeersWith(st, [far1, far2], bit, all);
                        if (!targets.length) continue;
                        return {
                            tech: 'two_string_kite', digits: [d], cells: all, base: [r.unit, c.unit],
                            roof: [far1, far2], floor: [near1, near2], unit: 2 * n + geo.cellBox[near1],
                            elim: elimList(targets, bit)
                        };
                    }
                }
            }
        }
        return null;
    }

    // ── Wings ────────────────────────────────────────────────────────────────
    // XY-Wing: a pivot {x,y} sees {x,z} and {y,z}. Whatever the pivot is, one
    // of the two wings is z.  XYZ-Wing: the pivot holds z too, so only cells
    // that see all three lose z.
    function wing(st, withZ) {
        var geo = st.geo, cells = geo.cells;
        for (var p = 0; p < cells; p++) {
            var pm = st.cand[p];
            if (st.val[p] || POP[pm] !== (withZ ? 3 : 2)) continue;
            var peers = geo.peers[p], wings = [];
            for (var k = 0; k < peers.length; k++) {
                var w = peers[k], wm = st.cand[w];
                if (st.val[w] || POP[wm] !== 2) continue;
                if (withZ ? ((wm & pm) === wm) : (POP[wm & pm] === 1)) wings.push(w);
            }
            for (var i = 0; i < wings.length; i++) {
                for (var j = i + 1; j < wings.length; j++) {
                    var a = st.cand[wings[i]], b = st.cand[wings[j]];
                    if (a === b) continue;
                    var z = a & b;
                    if (POP[z] !== 1) continue;
                    if (withZ) {
                        if ((a | b) !== pm) continue;
                    } else {
                        if (z & pm) continue;
                        if (((a | b) & ~z) !== pm) continue;
                    }
                    var from = withZ ? [p, wings[i], wings[j]] : [wings[i], wings[j]];
                    var all = [p, wings[i], wings[j]];
                    var targets = commonPeersWith(st, from, z, all);
                    if (!targets.length) continue;
                    return {
                        tech: withZ ? 'xyz_wing' : 'xy_wing', cells: all, pivot: p, wings: [wings[i], wings[j]],
                        digits: [LOW[z]], pivotDigits: digitsOf(pm), elim: elimList(targets, z)
                    };
                }
            }
        }
        return null;
    }

    // W-Wing: two cells with the same pair {a,b} that do not see each other,
    // joined by a strong link on a. They cannot both be a → one of them is b.
    function wWing(st) {
        var geo = st.geo, cells = geo.cells, n = geo.n, bi = [], i, j;
        for (i = 0; i < cells; i++) if (!st.val[i] && POP[st.cand[i]] === 2) bi.push(i);
        for (i = 0; i < bi.length; i++) {
            for (j = i + 1; j < bi.length; j++) {
                var A = bi[i], B = bi[j];
                if (st.cand[A] !== st.cand[B] || geo.sees[A * cells + B]) continue;
                var pair = digitsOf(st.cand[A]);
                for (var t = 0; t < 2; t++) {
                    var link = 1 << (pair[t] - 1), out = 1 << (pair[1 - t] - 1);
                    var targets = commonPeersWith(st, [A, B], out, [A, B]);
                    if (!targets.length) continue;
                    var links = strongLinks(st, link, 0, 3 * n);
                    for (var k = 0; k < links.length; k++) {
                        var L = links[k];
                        if (L.a === A || L.a === B || L.b === A || L.b === B) continue;
                        var ok = (geo.sees[L.a * cells + A] && geo.sees[L.b * cells + B]) ||
                                 (geo.sees[L.a * cells + B] && geo.sees[L.b * cells + A]);
                        if (!ok) continue;
                        return {
                            tech: 'w_wing', cells: [A, B, L.a, L.b], wings: [A, B], link: [L.a, L.b], unit: L.unit,
                            digits: [pair[1 - t]], linkDigit: pair[t], elim: elimList(targets, out)
                        };
                    }
                }
            }
        }
        return null;
    }

    // ── Simple colouring ─────────────────────────────────────────────────────
    // Chain the strong links of one digit and paint the cells in two colours:
    // all cells of one colour are the digit, all of the other are not.
    //   wrap — two cells of one colour see each other → that colour is false
    //   trap — a cell outside sees both colours → it cannot be the digit
    function simpleColoring(st) {
        var geo = st.geo, n = geo.n, cells = geo.cells;
        for (var d = 1; d <= n; d++) {
            var bit = 1 << (d - 1), links = strongLinks(st, bit, 0, 3 * n);
            if (links.length < 2) continue;
            var adj = {}, k;
            for (k = 0; k < links.length; k++) {
                (adj[links[k].a] || (adj[links[k].a] = [])).push(links[k].b);
                (adj[links[k].b] || (adj[links[k].b] = [])).push(links[k].a);
            }
            var colour = {};
            for (var start in adj) {
                if (!Object.prototype.hasOwnProperty.call(adj, start) || colour[start] !== undefined) continue;
                var groups = [[], []], queue = [+start];
                colour[start] = 0;
                while (queue.length) {
                    var cur = queue.shift();
                    groups[colour[cur]].push(cur);
                    var next = adj[cur];
                    for (k = 0; k < next.length; k++) {
                        if (colour[next[k]] === undefined) { colour[next[k]] = 1 - colour[cur]; queue.push(next[k]); }
                    }
                }
                if (groups[0].length + groups[1].length < 3) continue;
                groups[0].sort(function (a, b) { return a - b; });
                groups[1].sort(function (a, b) { return a - b; });
                for (var g = 0; g < 2; g++) {
                    var grp = groups[g];
                    for (var i = 0; i < grp.length; i++) {
                        for (var j = i + 1; j < grp.length; j++) {
                            if (geo.sees[grp[i] * cells + grp[j]]) {
                                return {
                                    tech: 'simple_coloring', kind: 'wrap', digits: [d], cells: groups[0].concat(groups[1]),
                                    colorA: groups[1 - g], colorB: grp, clash: [grp[i], grp[j]], elim: elimList(grp, bit)
                                };
                            }
                        }
                    }
                }
                var all = groups[0].concat(groups[1]), trapped = [];
                for (var c = 0; c < cells; c++) {
                    if (!(st.cand[c] & bit) || all.indexOf(c) >= 0) continue;
                    var seesA = false, seesB = false;
                    for (k = 0; k < groups[0].length; k++) if (geo.sees[c * cells + groups[0][k]]) { seesA = true; break; }
                    if (!seesA) continue;
                    for (k = 0; k < groups[1].length; k++) if (geo.sees[c * cells + groups[1][k]]) { seesB = true; break; }
                    if (seesB) trapped.push(c);
                }
                if (trapped.length) {
                    return {
                        tech: 'simple_coloring', kind: 'trap', digits: [d], cells: all,
                        colorA: groups[0], colorB: groups[1], elim: elimList(trapped, bit)
                    };
                }
            }
        }
        return null;
    }

    // ── XY-Chain ─────────────────────────────────────────────────────────────
    // A walk over two-candidate cells: "if the first is not z it is a, so the
    // next is b, so …, so the last one is z". Either end is z → a cell that
    // sees both ends is not. Shortest chains first.
    function xyChain(st, maxLen) {
        var geo = st.geo, cells = geo.cells, bi = [], i;
        maxLen = maxLen || 8;
        for (i = 0; i < cells; i++) if (!st.val[i] && POP[st.cand[i]] === 2) bi.push(i);
        if (bi.length < 3) return null;
        var best = null;
        // Breadth-first over "cell is this digit": the first time an end is
        // reached is its shortest chain, and each cell is entered at most once
        // per digit, so the search is small however long the chains are.
        var depth = new Int8Array(cells * 2), parent = new Int16Array(cells * 2), queue = new Int16Array(cells * 2);
        for (i = 0; i < bi.length; i++) {
            var s = bi[i], pair = digitsOf(st.cand[s]);
            for (var t = 0; t < 2; t++) {
                var zbit = 1 << (pair[t] - 1), carry = 1 << (pair[1 - t] - 1);
                depth.fill(0);
                // A node is cell*2 + (0 → the cell's lower digit, 1 → its higher one).
                var startNode = s * 2 + (1 - t), head = 0, tail = 0;
                depth[startNode] = 1; parent[startNode] = -1;
                queue[tail++] = startNode;
                while (head < tail) {
                    var node = queue[head++], cell = node >> 1, dep = depth[node];
                    if (dep >= maxLen) continue;
                    var cm = st.cand[cell];
                    var isBit = (node & 1) ? (cm & ~(cm & -cm)) : (cm & -cm);
                    var peers = geo.peers[cell];
                    for (var k = 0; k < peers.length; k++) {
                        var nx = peers[k], nm = st.cand[nx];
                        if (st.val[nx] || POP[nm] !== 2 || !(nm & isBit) || nx === s) continue;
                        var rest = nm & ~isBit;
                        var nn = nx * 2 + (rest === (nm & -nm) ? 0 : 1);
                        if (depth[nn]) continue;
                        depth[nn] = dep + 1; parent[nn] = node;
                        queue[tail++] = nn;
                        if (rest !== zbit || dep + 1 < 3 || nx < s) continue;
                        if (best && best.cells.length <= dep + 1) continue;
                        var path = [], p = nn, twice = false;
                        while (p >= 0) {
                            if (path.indexOf(p >> 1) >= 0) twice = true;
                            path.unshift(p >> 1);
                            p = parent[p];
                        }
                        if (twice) continue;
                        var targets = commonPeersWith(st, [s, nx], zbit, path);
                        if (targets.length) {
                            best = { tech: 'xy_chain', cells: path, digits: [LOW[zbit]], ends: [s, nx], elim: elimList(targets, zbit) };
                        }
                    }
                }
            }
        }
        return best;
    }

    // ── Unique rectangle, type 1 ─────────────────────────────────────────────
    // Four cells in two rows, two columns and two boxes; three hold only {a,b}.
    // If the fourth were a or b too, the pair could be swapped and the puzzle
    // would have two solutions. Sound only when one solution is a proven fact.
    function uniqueRectangle(st) {
        var geo = st.geo, n = geo.n;
        for (var r1 = 0; r1 < n; r1++) for (var r2 = r1 + 1; r2 < n; r2++) {
            for (var c1 = 0; c1 < n; c1++) for (var c2 = c1 + 1; c2 < n; c2++) {
                var q = [r1 * n + c1, r1 * n + c2, r2 * n + c1, r2 * n + c2];
                if (st.val[q[0]] || st.val[q[1]] || st.val[q[2]] || st.val[q[3]]) continue;
                var boxes = {};
                boxes[geo.cellBox[q[0]]] = 1; boxes[geo.cellBox[q[1]]] = 1; boxes[geo.cellBox[q[2]]] = 1; boxes[geo.cellBox[q[3]]] = 1;
                if (Object.keys(boxes).length !== 2) continue;
                for (var odd = 0; odd < 4; odd++) {
                    var pairMask = st.cand[q[(odd + 1) % 4]];
                    if (POP[pairMask] !== 2) continue;
                    if (st.cand[q[(odd + 2) % 4]] !== pairMask || st.cand[q[(odd + 3) % 4]] !== pairMask) continue;
                    var m = st.cand[q[odd]];
                    if ((m & pairMask) !== pairMask || m === pairMask) continue;
                    return {
                        tech: 'unique_rectangle', cells: q.slice(), digits: digitsOf(pairMask), target: q[odd],
                        elim: [[q[odd], pairMask]]
                    };
                }
            }
        }
        return null;
    }

    // ── Beyond the pattern library ───────────────────────────────────────────
    // Fill in the singles that follow from a position; stop at a contradiction.
    // Returns the forced moves with what each one rests on, so the explanation
    // can show only the moves that lead to the dead end.
    function propagate(st, trail) {
        for (;;) {
            var bad = broken(st);
            if (bad) return bad;
            if (!st.left) return null;
            var step = nakedSingle(st) || hiddenSingle(st);
            if (!step) return null;
            trail.push({ cell: step.place[0][0], digit: step.place[0][1], unit: step.tech === 'hidden_single' ? step.unit : -1 });
            applyStep(st, step);
        }
    }

    // Forcing: "suppose this cell is d" → the singles that follow run into a
    // wall → the cell is not d. Two-candidate cells first: there a refuted
    // digit hands over the answer.
    function forcing(st) {
        var geo = st.geo, cells = geo.cells, order = [], i;
        for (i = 0; i < cells; i++) if (!st.val[i]) order.push(i);
        order.sort(function (a, b) { return (POP[st.cand[a]] - POP[st.cand[b]]) || (a - b); });
        var best = null;
        for (i = 0; i < order.length; i++) {
            var cell = order[i], digits = digitsOf(st.cand[cell]);
            if (best && POP[st.cand[cell]] > 2) break;
            for (var k = 0; k < digits.length; k++) {
                var trial = cloneState(st), trail = [];
                place(trial, cell, digits[k]);
                var wall = propagate(trial, trail);
                if (!wall) continue;
                if (!best || trail.length < best.chain.length) {
                    best = {
                        tech: 'forcing', cells: [cell], digits: [digits[k]], chain: trail, wall: wall,
                        elim: [[cell, 1 << (digits[k] - 1)]]
                    };
                }
            }
        }
        return best;
    }

    // The honest last resort: no deduction of ours applies, the digit comes
    // from the solution and the step says so.
    function brute(st, solution) {
        if (!solution) return null;
        var best = -1;
        for (var i = 0; i < st.geo.cells; i++) {
            if (st.val[i]) continue;
            if (best < 0 || POP[st.cand[i]] < POP[st.cand[best]]) best = i;
        }
        if (best < 0) return null;
        return { tech: 'brute', cells: [best], digits: [solution[best]], place: [[best, solution[best]]] };
    }

    var FINDERS = {
        full_house: fullHouse,
        hidden_single: hiddenSingle,
        naked_single: nakedSingle,
        pointing: function (st) { return locked(st, true); },
        claiming: function (st) { return locked(st, false); },
        naked_pair: function (st) { return nakedSubset(st, 2); },
        hidden_pair: function (st) { return hiddenSubset(st, 2); },
        naked_triple: function (st) { return nakedSubset(st, 3); },
        hidden_triple: function (st) { return hiddenSubset(st, 3); },
        x_wing: function (st) { return fish(st, 2); },
        skyscraper: skyscraper,
        two_string_kite: twoStringKite,
        xy_wing: function (st) { return wing(st, false); },
        naked_quad: function (st) { return nakedSubset(st, 4); },
        hidden_quad: function (st) { return hiddenSubset(st, 4); },
        swordfish: function (st) { return fish(st, 3); },
        w_wing: wWing,
        simple_coloring: simpleColoring,
        xyz_wing: function (st) { return wing(st, true); },
        jellyfish: function (st) { return fish(st, 4); },
        xy_chain: function (st) { return xyChain(st, 8); },
        unique_rectangle: function (st) { return st.unique ? uniqueRectangle(st) : null; },
        forcing: forcing,
        brute: function (st, opts) { return brute(st, opts && opts.solution); }
    };

    // The easiest step available in the position, never above opts.maxTier.
    function nextStep(st, opts, minTier) {
        opts = opts || {};
        var maxTier = opts.maxTier || 5;
        if (!st.left) return null;
        for (var i = 0; i < TECHNIQUES.length; i++) {
            var name = TECHNIQUES[i][0], tier = TECHNIQUES[i][1];
            if (tier > maxTier) break;
            if (minTier && tier < minTier) continue;
            var step = FINDERS[name](st, opts);
            if (step) { step.tier = tier; return step; }
        }
        return null;
    }

    // The singles of a whole solve without building a step for each: the same
    // order of preference as nextStep (a digit's only place in a unit before a
    // cell's only digit), just counted instead of described. A generator runs
    // thousands of solves, and nine steps in ten are singles.
    function runSingles(st, maxTier, out) {
        var geo = st.geo, n = geo.n, units = geo.units, cand = st.cand, val = st.val;
        for (;;) {
            var moved = false, u, k;
            for (u = 0; u < units.length && st.left; u++) {
                var unit = units[u], once = 0, twice = 0, empty = 0;
                for (k = 0; k < n; k++) {
                    var m = cand[unit[k]];
                    if (!val[unit[k]]) empty++;
                    twice |= once & m;
                    once |= m;
                }
                var single = once & ~twice;
                while (single) {
                    var bit = single & -single;
                    single ^= bit;
                    for (k = 0; k < n; k++) {
                        if (cand[unit[k]] & bit) {
                            place(st, unit[k], LOW[bit]);
                            var name = empty === 1 ? 'full_house' : 'hidden_single';
                            out.techs[name] = (out.techs[name] || 0) + 1;
                            out.score += WEIGHT[name];
                            out.count++;
                            empty--;
                            if (out.tier < 1) out.tier = 1;
                            moved = true;
                            break;
                        }
                    }
                }
            }
            if (moved) continue;
            if (!st.left || maxTier < 2) return;
            for (k = 0; k < geo.cells; k++) {
                if (!val[k] && POP[cand[k]] === 1) {
                    place(st, k, LOW[cand[k]]);
                    out.techs.naked_single = (out.techs.naked_single || 0) + 1;
                    out.score += WEIGHT.naked_single;
                    out.count++;
                    if (out.tier < 2) out.tier = 2;
                    moved = true;
                    break;
                }
            }
            if (!moved) return;
        }
    }

    // Solve by logic alone. tier — the level the puzzle demands; techs — how
    // many times each technique was needed; score — the summed thinking cost.
    //   opts.maxTier   do not reach above this level (5 = every pattern we have)
    //   opts.unique    one solution is proven → uniqueness-based steps are sound
    //   opts.solution  enables the last-resort step for a foreign puzzle
    //   opts.steps     true → keep the whole list of steps (slower: every single is a step)
    function grade(geo, values, opts) {
        opts = opts || {};
        var st = state(geo, values), maxTier = opts.maxTier || 5;
        st.unique = !!opts.unique;
        var out = { solved: false, tier: 0, techs: {}, score: 0, count: 0, steps: opts.steps ? [] : null, stuck: false };
        while (st.left) {
            var step;
            if (out.steps) {
                step = nextStep(st, opts);
            } else {
                runSingles(st, maxTier, out);
                if (!st.left) break;
                step = maxTier >= 3 ? nextStep(st, opts, 3) : null;
            }
            if (!step) { out.stuck = true; break; }
            applyStep(st, step);
            out.count++;
            out.techs[step.tech] = (out.techs[step.tech] || 0) + 1;
            out.score += WEIGHT[step.tech] || 0;
            if (step.tier > out.tier) out.tier = step.tier;
            if (out.steps) out.steps.push(step);
            if (!step.place && broken(st)) { out.stuck = true; break; }
        }
        out.solved = !st.left && !broken(st);
        out.values = Array.prototype.slice.call(st.val);
        return out;
    }

    S.TECHNIQUES = TECHNIQUES;
    S.TIER = TIER;
    S.WEIGHT = WEIGHT;
    S.state = state;
    S.cloneState = cloneState;
    S.place = place;
    S.applyStep = applyStep;
    S.broken = broken;
    S.nextStep = nextStep;
    S.grade = grade;
    S.find = FINDERS;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
